import { z } from "zod";
import {
  homePathForPortalModule,
  normalizePortalModule,
  resolveDefaultPortalModule,
  type PortalModule,
} from "./portalModules";

/** Portal staff roles stored on `users.role` (primary) and optionally in `users.secondaryRolesJson`. */
export const USER_ROLES = [
  "sales",
  "admin",
  "service",
  "finance",
  "programming",
  "installer",
  "marketing",
  "inventory",
  "club",
] as const;

export type UserRole = (typeof USER_ROLES)[number];

/** Shared Zod schema for `users.role` — keep in sync with drizzle `users.role` enum. */
export const portalUserRoleSchema = z.enum(USER_ROLES);

export const MARKETING_ROLE = "marketing" as const;
export const INVENTORY_ROLE = "inventory" as const;

export type UserRoleFields = {
  role?: string | null;
  secondaryRolesJson?: unknown;
};

/** Trim + lowercase so `Sales` / padded enum leftovers still match stored roles. */
export function normalizeUserRole(role: string | null | undefined): string {
  return String(role ?? "").trim().toLowerCase();
}

export function isMarketingRole(role: string | undefined | null): boolean {
  return normalizeUserRole(role) === MARKETING_ROLE;
}

export function isInventoryRole(role: string | undefined | null): boolean {
  return normalizeUserRole(role) === INVENTORY_ROLE;
}

/** Parse secondary roles JSON safely; invalid entries are dropped. */
export function parseSecondaryRoles(raw: unknown): UserRole[] {
  if (raw == null) return [];
  let arr: unknown[] = [];
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) arr = parsed;
    } catch {
      return [];
    }
  } else if (Array.isArray(raw)) {
    arr = raw;
  } else {
    return [];
  }
  const out: UserRole[] = [];
  const seen = new Set<string>();
  for (const item of arr) {
    const r = normalizeUserRole(String(item ?? ""));
    if (!USER_ROLES.includes(r as UserRole)) continue;
    if (seen.has(r)) continue;
    seen.add(r);
    out.push(r as UserRole);
  }
  return out;
}

/** Primary + secondary roles as a set (secondary never replaces primary). */
export function resolveUserRoleSet(user: UserRoleFields | null | undefined): Set<UserRole> {
  const set = new Set<UserRole>();
  if (!user) return set;
  const primary = normalizeUserRole(user.role);
  if (USER_ROLES.includes(primary as UserRole)) set.add(primary as UserRole);
  for (const r of parseSecondaryRoles(user.secondaryRolesJson)) {
    set.add(r);
  }
  return set;
}

/** True if primary or any secondary role matches. */
export function hasRoleCapability(
  user: UserRoleFields | null | undefined,
  allowed: readonly UserRole[] | readonly string[],
): boolean {
  if (!user || allowed.length === 0) return false;
  const roles = resolveUserRoleSet(user);
  return allowed.some((r) => roles.has(r as UserRole));
}

/** Secondary roles excluding the primary (for admin UI persistence). */
export function normalizeSecondaryRolesForSave(
  primary: UserRole,
  secondary: readonly string[],
): UserRole[] {
  const seen = new Set<string>();
  const out: UserRole[] = [];
  for (const raw of secondary) {
    const r = normalizeUserRole(raw);
    if (!USER_ROLES.includes(r as UserRole)) continue;
    if (r === primary) continue;
    if (seen.has(r)) continue;
    seen.add(r);
    out.push(r as UserRole);
  }
  return out;
}

export function canAccessKnowledgeCentre(role: string | undefined | null): boolean {
  return role === "admin" || isMarketingRole(role);
}

export function canAccessProposalIntro(role: string | undefined | null): boolean {
  return role === "admin" || isMarketingRole(role);
}

/**
 * Default landing path after login (or bare `/`).
 * Prefer `homePathForUser` when secondary roles / default module are available.
 */
export function homePathForRole(role: string | undefined | null): string {
  if (isMarketingRole(role)) return "/admin/articles";
  if (isInventoryRole(role)) return "/procurement";
  return "/";
}

/** Post-login redirect — marketing lands on Knowledge Centre, except Club booking deep links. */
export function postLoginPathForRole(
  role: string | undefined | null,
  intendedPath: string,
): string {
  if (isMarketingRole(role)) {
    const pathOnly = String(intendedPath || "/").split("?")[0] ?? "";
    const path = pathOnly.replace(/\/+$/, "") || "/";
    if (path === "/club-bookings") return intendedPath;
    return "/admin/articles";
  }
  return intendedPath;
}

export type HomePathUser = UserRoleFields & {
  defaultPortalModule?: string | null;
};

/**
 * Resolve home path from default portal module preference + authorised modules.
 * Marketing remains confined to Knowledge Centre.
 * Inventory primary role defaults to Procurement when no preference is saved.
 */
export function homePathForUser(
  user: HomePathUser | null | undefined,
  canAccessModule: (module: PortalModule) => boolean,
): string {
  if (!user) return "/";
  if (isMarketingRole(user.role)) return "/admin/articles";

  const saved = normalizePortalModule(user.defaultPortalModule);
  if (saved && canAccessModule(saved)) {
    return homePathForPortalModule(saved);
  }

  // No valid saved preference — inventory primary defaults to Procurement.
  if (isInventoryRole(user.role) && canAccessModule("procurement")) {
    return homePathForPortalModule("procurement");
  }

  const module = resolveDefaultPortalModule({
    saved: null,
    canAccess: canAccessModule,
  });
  return homePathForPortalModule(module);
}

/**
 * Post-login: honour intended deep link when authorised; otherwise default module home.
 * Marketing stays on Knowledge Centre, except an authorised Club bookings deep link.
 */
export function postLoginPathForUser(
  user: HomePathUser | null | undefined,
  intendedPath: string,
  canAccessModule: (module: PortalModule) => boolean,
): string {
  if (!user) return "/login";
  const intended = String(intendedPath || "/").trim() || "/";
  const pathOnly = intended.split("?")[0] ?? intended;
  if (isMarketingRole(user.role)) {
    if (isClubBookingsPath(pathOnly)) return intended;
    return "/admin/articles";
  }
  // Bare root → apply default module preference.
  if (pathOnly === "/" || pathOnly === "") {
    return homePathForUser(user, canAccessModule);
  }
  return intended;
}

function isClubBookingsPath(pathname: string): boolean {
  const path = (pathname.split("?")[0] ?? pathname).replace(/\/+$/, "") || "/";
  return path === "/club-bookings";
}

/** Paths marketing staff may access while signed in. */
export function isPathAllowedForMarketing(pathname: string): boolean {
  const path = pathname.split("?")[0] ?? pathname;
  if (path === "/login" || path.startsWith("/reset-password") || path.startsWith("/invite/")) {
    return true;
  }
  if (path.startsWith("/admin/articles")) return true;
  if (path.startsWith("/admin/proposals/intro")) return true;
  if (path.startsWith("/articles")) return true;
  if (path.startsWith("/my-digital-card")) return true;
  if (path === "/club-bookings" || path.startsWith("/club-bookings/")) return true;
  return false;
}

/** tRPC procedure paths marketing may call (protected procedures only). */
export const MARKETING_ALLOWED_TRPC_PREFIXES = [
  "knowledgeArticles.",
  "proposalIntroSettings.",
  "digitalCards.",
  "cleoOperationsDesk.",
  "clubBookings.",
  "system.reportStaffForegroundIssue",
] as const;

export function isTrpcPathAllowedForMarketing(path: string): boolean {
  return MARKETING_ALLOWED_TRPC_PREFIXES.some((prefix) => path.startsWith(prefix));
}
